/** @format */

import { Chain } from '@shared/data-access/interfaces';
import { EvergreenTemplateData, GamData } from './google-ad-manager.interface';
import { EvergreenTemplateComponent } from '../evergreen-template/evergreen-template.component';
import {
	Component,
	Input,
	OnInit,
	Inject,
	HostListener,
	input,
	signal,
	computed
} from '@angular/core';

import { AnalyticsService, Logger, UtilityService, WINDOW } from '@shared/data-access/services';
import { CommonModule } from '@angular/common';

import {
	homefieldIconAlertInfoOutlined,
	HomefieldIconLibraryModule,
	homefieldIconSignCircle,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
	selector: 'shared-components-google-ad-manager',
	templateUrl: './google-ad-manager.component.html',
	styleUrl: './google-ad-manager.component.scss',
	imports: [CommonModule, HomefieldIconLibraryModule, EvergreenTemplateComponent],
	standalone: true
})
export class GoogleAdManagerComponent implements OnInit {
	@Input() data: string;
	@Input() breadcrumbData = null;
	@Input() addlData: any = null;
	showSponsoredLabel = input<boolean>(false);
	readonly hasTopMargin = input(true);
	enableTooltipSurvey = input(true);

	gamData: GamData | undefined;
	isDsgStorePage: boolean;
	readonly hideAdLabel = signal<boolean>(true);
	readonly showSponsoredLabelUpdated = signal<boolean>(false);
	readonly shouldShowSponsoredLabel = computed(
		() => this.showSponsoredLabelUpdated() && !this.hideAdLabel()
	);

	/* Ad Carousel variables */
	readonly enableAdCarousel = input(false);
	readonly isNarrowBanner = input<boolean>(false);
	currentSlide = signal<number>(0);
	carouselBaseUrl = 'https://images.dickssportinggoods.com/medianetwork/';
	evergreenCampaign: number;
	imageName: string;
	templateData = signal<EvergreenTemplateData | null>(null);

	constructor(
		private readonly analyticsService: AnalyticsService,
		@Inject(WINDOW) protected window: Window,
		private readonly chain: Chain,
		homefieldIconsLibraryService: HomefieldIconsLibraryService,
		private readonly utilityService: UtilityService,
		private readonly http: HttpClient,
		private readonly logger: Logger
	) {
		homefieldIconsLibraryService.registerIcons([
			homefieldIconAlertInfoOutlined,
			homefieldIconSignCircle
		]);
	}

	ngOnInit(): void {
		this.showSponsoredLabelUpdated.set(
			this.addlData?.data[0]?.showSponsoredLabel ?? this.showSponsoredLabel()
		);
		if (this.enableAdCarousel()) {
			this.evergreenCampaign = Math.floor(Math.random() * 5);
			this.getEvergreenJson().subscribe({
				next: (templateData) => {
					this.templateData.set(templateData);
				},
				error: (error) => {
					this.logger.error('Error loading Evergreen JSON:', error);
				}
			});
		}

		this.utilityService.deferOnPrerendering(() => this.triggerAnalytics());

		this.isDsgStorePage =
			this.window.location?.href.endsWith('/s/stores') &&
			this.chain.chainIdentifierAbbr === 'dsg';
	}

	private triggerAnalytics(): void {
		const analyticsEventObject = { AdSpotIDs: this.data };
		// add breadcrumbs data to analytics event if category page has breadcrumbs
		if (this.breadcrumbData) {
			analyticsEventObject['Breadcrumbs'] = this.breadcrumbData;
		}

		this.analyticsService.triggerAnalyticsEvent('AdSpotsReady', analyticsEventObject);
	}

	private checkAdVisibility(): void {
		const banner = this.window.document.getElementById(this.data);
		const iframe = banner?.querySelector<HTMLIFrameElement>('iframe');
		if (!iframe) {
			this.hideAdLabel.set(true);
			return;
		}

		const widthAttr = parseInt(iframe.getAttribute('width') || '0');
		const heightAttr = parseInt(iframe.getAttribute('height') || '0');

		if (widthAttr > 0 || heightAttr > 0) {
			this.hideAdLabel.set(false);
			if (this.enableAdCarousel()) {
				this.currentSlide.set(1);
			}
		}
	}

	loadSurvey(): void {
		if (typeof this.window.KAMPYLE_ONSITE_SDK !== 'undefined' && this.enableTooltipSurvey()) {
			this.window.gamData = this.gamData;
			if (this.chain.chainIdentifierAbbr === 'dsg') {
				this.window.KAMPYLE_ONSITE_SDK.showForm(15821);
			} else if (this.chain.chainIdentifierAbbr === 'gg') {
				this.window.KAMPYLE_ONSITE_SDK.showForm(16385);
			}
		}
	}

	@HostListener('window:message', ['$event'])
	onMessage(event: MessageEvent): void {
		if (!this.enableTooltipSurvey() && !this.enableAdCarousel()) {
			return;
		}
		const allowedHosts = ['safeframe.googlesyndication.com'];
		let originHost: string;
		try {
			originHost = new URL(event.origin).hostname;
		} catch (e) {
			return;
		}

		if (originHost.endsWith(allowedHosts[0])) {
			const adData = event.data;
			let parsedAdData;
			// Google pumps out a lot of events with this hostname, sometimes the data is not json
			try {
				parsedAdData = JSON.parse(adData);
			} catch (e) {
				return;
			}

			if (parsedAdData?.Header && !this.gamData) {
				this.gamData = parsedAdData;
			}

			this.checkAdVisibility();
		}
	}

	private getEvergreenJson(): Observable<EvergreenTemplateData> {
		this.imageName = this.isNarrowBanner() ? 'narrow' : 'ribbon';
		return this.http
			.get<EvergreenTemplateData>(
				`${this.carouselBaseUrl}${this.imageName}_${this.evergreenCampaign}.json`
			)
			.pipe(
				map((templateData: EvergreenTemplateData) => {
					templateData.MAIN_IMG = `${this.carouselBaseUrl}${templateData.MAIN_IMG}`;
					return templateData;
				})
			);
	}
}
